import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import  {Comment, ViewComment } from "./Comment";
import { ActionMenu, DataNotFound } from "../utils";

const WarehouseTripInsightsTable = ({ data }) => {
  const todayData = data?.today || [];
  const yesterdayData = data?.yesterday || [];

  /* --------------------------------------------------
     SORTING STATE (UI ONLY)
  -------------------------------------------------- */
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <span style={{ marginLeft: 4, color: "#888" }}>⇅</span>;
    }
    return sortConfig.direction === "asc" ? (
      <span style={{ marginLeft: 4, color: "#2e7d32" }}>▲</span>
    ) : (
      <span style={{ marginLeft: 4, color: "#d32f2f" }}>▼</span>
    );
  };

  /* --------------------------------------------------
     COMMENT MODAL STATE
  -------------------------------------------------- */
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const closeModals = () => {
    setAddModalOpen(false);
    setViewModalOpen(false);
    setActiveRow(null);
  };

  /* --------------------------------------------------
     YESTERDAY MAP
  -------------------------------------------------- */
  const yesterdayMap = useMemo(() => {
    const map = {};
    yesterdayData.forEach((d) => {
      if (d?.plantId) map[d.plantId] = d;
    });
    return map;
  }, [yesterdayData]);

  /* --------------------------------------------------
     HELPERS
  -------------------------------------------------- */
  const percent = (val, total) =>
    total ? ((Number(val) / Number(total)) * 100).toFixed(1) : 0;

  const tripDelta = (today, yesterday) =>
    yesterday === undefined || yesterday === null
      ? 0
      : Number(today) - Number(yesterday);

  const renderTripDelta = (value) => {
    if (value > 0)
      return <span style={{ color: "#2e7d32", fontWeight: 600 }}>▲ +{value}</span>;
    if (value < 0)
      return <span style={{ color: "#d32f2f", fontWeight: 600 }}>▼ {value}</span>;
    return <span style={{ color: "#555", fontWeight: 600 }}>0</span>;
  };

  /* --------------------------------------------------
     BASE ROWS (UNSORTED)
  -------------------------------------------------- */
  const rows = useMemo(() => {
    return todayData.map((d) => {
      const y = yesterdayMap[d.plantId] || {};
      return {
        plantId: d.plantId,
        plantName: d.plantName,
        total: d.total,

        ongoingTotal: d.ongoing,
        ongoingPercent: Number(percent(d.ongoing, d.total)),
        ongoingDelta: tripDelta(d.ongoing, y.ongoing),

        completedTotal: d.completed,
        completedPercent: Number(percent(d.completed, d.total)),
        completedDelta: tripDelta(d.completed, y.completed),
      };
    });
  }, [todayData, yesterdayMap]);

  /* --------------------------------------------------
     SORTED ROWS (FOR UI ONLY)
  -------------------------------------------------- */
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (typeof aVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortConfig.direction === "asc"
        ? aVal - bVal
        : bVal - aVal;
    });
  }, [rows, sortConfig]);

  /* --------------------------------------------------
     STYLES
  -------------------------------------------------- */
  const thStyle = {
    padding: "8px 8px",
    color: "var(--text-color)",
    border: "1px solid #bdbdbd",
    fontWeight: 600,
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "7px 8px",
    border: "1px solid #d0d0d0",
    textAlign: "center",
    whiteSpace: "nowrap",
  };
  /* --------------------------------------------------
     RENDER
  -------------------------------------------------- */
  return (
    <>

      <Box
        sx={{
          width: "100%",
          border: "1px solid #bdbdbd",
          borderRadius: "12px",
          maxHeight: "520px",
          overflow: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10, fontSize:"12px" }}>
            <tr>
              <th
                rowSpan={2}
                style={{ ...thStyle, textAlign: "left", cursor: "pointer" }}
                onClick={() => handleSort("plantName")}
              >
                Warehouse Name {getSortIcon("plantName")}
              </th>

              <th
                rowSpan={2}
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("total")}
              >
                Total {getSortIcon("total")}
              </th>

              <th colSpan={3} style={thStyle}>Ongoing</th>
              <th colSpan={3} style={thStyle}>Completed</th>
              <th rowSpan={2} style={thStyle}>Actions</th>
            </tr>

            <tr>
              <th
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("ongoingTotal")}
              >
                Total Ongoing {getSortIcon("ongoingTotal")}
              </th>

              <th
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("ongoingPercent")}
              >
                % Ongoing {getSortIcon("ongoingPercent")}
              </th>

              <th
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("ongoingDelta")}
              >
                Trips vs Yesterday {getSortIcon("ongoingDelta")}
              </th>

              <th
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("completedTotal")}
              >
                Total Completed {getSortIcon("completedTotal")}
              </th>

              <th
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("completedPercent")}
              >
                % Complete {getSortIcon("completedPercent")}
              </th>

              <th
                style={{ ...thStyle, cursor: "pointer" }}
                onClick={() => handleSort("completedDelta")}
              >
                Trips vs Yesterday {getSortIcon("completedDelta")}
              </th>
            </tr>
          </thead>
        {


        }
          <tbody style={{fontSize:"12px"}}>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    fontWeight: 600,

                  }}
                >
                  <DataNotFound/>
                </td>
              </tr>
            ) : (
              (sortedRows.map((r, i) => (
                <tr key={r.plantId || i} >
                  <td style={{ ...tdStyle, textAlign: "left" }}>{r.plantName}</td>
                  <td style={tdStyle}>{r.total}</td>

                  <td style={tdStyle}>{r.ongoingTotal}</td>
                  <td style={tdStyle}>{r.ongoingPercent}%</td>
                  <td style={tdStyle}>{renderTripDelta(r.ongoingDelta)}</td>

                  <td style={tdStyle}>{r.completedTotal}</td>
                  <td style={tdStyle}>{r.completedPercent}%</td>
                  <td style={tdStyle}>{renderTripDelta(r.completedDelta)}</td>

                  {/* ACTION MENU — NO SORTING */}
                  <td style={tdStyle}>
                    <ActionMenu
                      id={r.plantId}
                      handlers={{
                        view: () => {
                          setActiveRow(r);
                          setViewModalOpen(true);
                        },
                        addComment: () => {
                          setActiveRow(r);
                          setAddModalOpen(true);
                        }
                      }}
                    />
                  </td>
                </tr>
              )))
            )}
          </tbody>
        </table>
      </Box>

      {addModalOpen && (
        <Comment open={addModalOpen} plantData={activeRow} onClose={closeModals} />
      )}

      {viewModalOpen && (
        <ViewComment open={viewModalOpen} plantData={activeRow} onClose={closeModals} />
      )}
    </>
  );
};

export default WarehouseTripInsightsTable;
